import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { siteConfig } from '@/constants/site'

type ButtonVariant = 'primary' | 'outline' | 'honey' | 'light'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonStyleOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-plum text-white shadow-sm shadow-plum/20 hover:bg-plum-deep focus-visible:outline-plum',
    outline: 'border border-plum/35 bg-transparent text-plum hover:border-plum hover:bg-lavender/60 focus-visible:outline-plum',
    honey: 'bg-honey text-espresso hover:bg-[#E2C48F] focus-visible:outline-honey',
    light: 'bg-shell text-plum hover:bg-white focus-visible:outline-shell'
}

const sizeClasses: Record<ButtonSize, string> = {
    sm: 'min-h-10 px-5 py-2 text-sm',
    md: 'min-h-12 px-6 py-3 text-[0.95rem]',
    lg: 'min-h-14 px-7 py-3.5 text-base'
}

export const buttonClasses = ({ variant = 'primary', size = 'md', className }: ButtonStyleOptions = {}): string => {
    return cn(
        'inline-flex items-center justify-center gap-2 rounded-full text-center leading-snug font-medium transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-60',
        variantClasses[variant],
        sizeClasses[size],
        className
    )
}

interface ButtonLinkProps extends ButtonStyleOptions {
    href: string
    children: ReactNode
}

export const ButtonLink: FC<ButtonLinkProps> = ({ href, children, variant, size, className }): ReactNode => {
    return (
        <Link href={href} className={buttonClasses({ variant, size, className })}>
            {children}
        </Link>
    )
}

interface BookingButtonProps extends ButtonStyleOptions {
    children?: ReactNode
}

export const BookingButton: FC<BookingButtonProps> = ({ children = 'Book a Free Consult', variant, size, className }): ReactNode => {
    return (
        <a
            href={siteConfig.bookingUrl}
            target='_blank'
            rel='noopener noreferrer'
            className={buttonClasses({ variant, size, className })}
        >
            {children}
            <span className='sr-only'> (opens in a new tab)</span>
        </a>
    )
}
